// SEO "money" + local landing pages. Copy is drafted in the CardScience HQ
// premium-laboratory tone. Never guarantees a grade; every page carries
// the standard independent-grading disclaimer via ServiceLanding.

export const SEO_PAGES = {
  "pokemon-card-cleaning": {
    path: "/pokemon-card-cleaning",
    seoTitle: "Pokémon Card Cleaning & Grading Prep | CardScience HQ",
    metaDescription:
      "Professional Pokémon card cleaning and grading preparation. Individually evaluated, gentle-by-design surface care for Charizard, vintage WOTC and modern TCG — mail-in nationwide. No grade guaranteed.",
    serviceType: "Pokémon card cleaning",
    eyebrow: "Card Services · Pokémon",
    tone: "cyan",
    h1: "POKÉMON CARD CLEANING.\nPREPARED THE RIGHT WAY.",
    sub: "Vintage WOTC holos, modern TCG chase cards and everything in between — evaluated individually and cleaned only when it can be done responsibly. We prepare the card. The grader makes the call.",
    intro: [
      "Pokémon cards behave differently than most trading cards. Holofoil patterns, textured surfaces, edge whitening and factory print lines all respond differently to handling — which is exactly why a one-size-fits-all approach is the fastest way to damage a card.",
      "CardScience HQ evaluates every Pokémon submission on its own terms. We identify what is surface contamination that can be responsibly addressed, and what is a permanent defect that no process can remove.",
    ],
    impact: "WE DON'T FORCE A HOLO TO LOOK LIKE SOMETHING IT ISN'T.",
    sections: [
      {
        heading: "WHAT WE LOOK AT ON A POKÉMON CARD",
        paragraphs: [
          "Surface debris, fingerprints and residue on holo and non-holo areas. Edge and corner presentation. Factory gloss versus handling haze. Whitening that is structural versus surface-level.",
          "Each observation informs whether cleaning is appropriate — and when it isn't, we tell you.",
        ],
      },
    ],
    doList: [
      "Individually evaluate every card before any work begins",
      "Gentle surface cleaning where the card can tolerate it",
      "Careful preparation and preservation-minded handling",
      "Documented handling from intake to return",
    ],
    dontList: [
      "We do not promise a PSA 10 or any specific grade.",
      "We do not use harsh chemicals or direct heat.",
      "We do not trim, recolor or rebuild corners.",
      "We do not promise a grading company will accept your card.",
    ],
    faqs: [
      {
        q: "Should I clean a Pokémon card before grading?",
        a: "Sometimes. Surface debris and residue can be responsibly addressed, but many issues — scratches, print lines, structural whitening — are permanent. We evaluate each card individually and will tell you honestly if cleaning won't help.",
      },
      {
        q: "Will cleaning increase my card's grade?",
        a: "We never promise that. Independent grading companies determine grades under their own standards. Our role is to help an eligible card present at its best; the grade is theirs alone.",
      },
      {
        q: "Do you handle vintage WOTC holos and modern TCG cards?",
        a: "Yes. Both are evaluated individually. Vintage holos and modern textured surfaces require different handling, which is why we never apply a single process to everything.",
      },
    ],
    related: [
      { label: "Card Grading Prep", to: "/card-grading-prep" },
      { label: "How It Works", to: "/how-it-works" },
      { label: "Before & After", to: "/before-after" },
    ],
  },

  "sports-card-cleaning": {
    path: "/sports-card-cleaning",
    seoTitle: "Sports Card Cleaning & Grading Prep | CardScience HQ",
    metaDescription:
      "Professional sports card cleaning and grading preparation for football, basketball, hockey, soccer and more. Individually evaluated, gentle-by-design, mail-in nationwide. No grade guaranteed.",
    serviceType: "Sports card cleaning",
    eyebrow: "Card Services · Sports",
    tone: "blue",
    h1: "SPORTS CARD CLEANING.\nENGINEERED FOR PRESENTATION.",
    sub: "Prizm, Optic, Select, chrome and vintage cardboard each respond differently. We evaluate every card individually and prepare it responsibly for its best presentation.",
    intro: [
      "Modern chrome and optichrome cards scratch and haze in ways vintage cardboard never did. Vintage cards carry decades of surface history that can't be undone. Treating them the same way is how cards get ruined.",
      "CardScience HQ separates the cards we can responsibly help from the ones we cannot — and we're upfront about the difference before any work begins.",
    ],
    impact: "CLEANER DOES NOT MEAN PERFECT.",
    sections: [
      {
        heading: "MODERN CHROME VS. VINTAGE CARDBOARD",
        paragraphs: [
          "Chrome finishes (Prizm, Optic, Select, Chrome) show surface haze and fingerprints readily and require a delicate hand. Vintage stock is porous and unforgiving — aggressive handling causes irreversible damage.",
          "We match the approach to the card, never the other way around.",
        ],
      },
    ],
    doList: [
      "Individual evaluation of every card, modern or vintage",
      "Gentle, finish-appropriate surface care",
      "Preparation and preservation-minded handling",
      "Documented chain of custody",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not use harsh chemicals or direct heat.",
      "We do not trim, recolor or manufacture corners.",
      "We do not promise increased monetary value.",
    ],
    faqs: [
      {
        q: "Can you clean modern Prizm and Optic cards?",
        a: "Yes, with a delicate, finish-appropriate approach. Chrome finishes show haze and fingerprints and require careful handling. We evaluate each card individually before any work.",
      },
      {
        q: "Do you clean vintage sports cards?",
        a: "We evaluate them. Vintage cardboard is porous and unforgiving; sometimes the responsible answer is minimal handling or none at all. We tell you honestly what is and isn't appropriate.",
      },
      {
        q: "Will this improve my grade?",
        a: "We never promise that. Grades are determined independently by grading companies under their own standards. We prepare the card; the grade is not ours to give.",
      },
    ],
    related: [
      { label: "Baseball Card Cleaning", to: "/baseball-card-cleaning" },
      { label: "Card Grading Prep", to: "/card-grading-prep" },
      { label: "Before & After", to: "/before-after" },
    ],
  },

  "baseball-card-cleaning": {
    path: "/baseball-card-cleaning",
    seoTitle: "Baseball Card Cleaning & Grading Prep | CardScience HQ",
    metaDescription:
      "Professional baseball card cleaning and grading preparation — vintage Topps, Bowman rookies and modern refractors. Individually evaluated, gentle-by-design, mail-in nationwide. No grade guaranteed.",
    serviceType: "Baseball card cleaning",
    eyebrow: "Card Services · Baseball",
    tone: "cyan",
    h1: "BASEBALL CARD CLEANING.\nRESPECT FOR THE CARDBOARD.",
    sub: "From vintage Topps and Bowman rookies to modern refractors — each era of baseball card demands a different hand. We evaluate individually and prepare responsibly.",
    intro: [
      "Baseball cards span more history than almost any collectible category. A 1950s Topps and a 2023 refractor could not be more different in how they respond to handling.",
      "We treat every baseball submission as its own case, identifying surface issues that can be responsibly addressed and defects that are simply part of the card.",
    ],
    impact: "SOME CARDS SHOULD BE LEFT EXACTLY AS THEY ARE.",
    sections: [
      {
        heading: "VINTAGE TOPPS TO MODERN REFRACTORS",
        paragraphs: [
          "Vintage stock carries wax residue, print snow and decades of surface history. Modern refractors and chrome show haze and fingerprints. Each is handled on its own terms.",
          "When a vintage card's character is part of its value, the responsible choice is often to preserve it, not process it.",
        ],
      },
    ],
    doList: [
      "Era-appropriate individual evaluation",
      "Gentle surface care where responsible",
      "Preservation-first handling for vintage stock",
      "Documented handling throughout",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not use harsh chemicals or direct heat.",
      "We do not trim or rebuild vintage corners.",
      "We do not manufacture condition that isn't there.",
    ],
    faqs: [
      {
        q: "Should vintage baseball cards be cleaned before grading?",
        a: "Not always. Vintage cardboard is fragile and its surface history is often part of its character. We evaluate each card and frequently recommend preservation over processing.",
      },
      {
        q: "Can you prep modern baseball rookies and refractors?",
        a: "Yes. Modern chrome and refractor finishes are evaluated and handled with a delicate, finish-appropriate approach before grading prep.",
      },
      {
        q: "Will cleaning guarantee a higher grade?",
        a: "No. Independent grading companies set and apply their own standards. We prepare the card responsibly; we never guarantee a grade.",
      },
    ],
    related: [
      { label: "Sports Card Cleaning", to: "/sports-card-cleaning" },
      { label: "Card Grading Prep", to: "/card-grading-prep" },
      { label: "Results", to: "/results" },
    ],
  },

  "card-grading-prep": {
    path: "/card-grading-prep",
    seoTitle: "Card Grading Preparation Service | CardScience HQ",
    metaDescription:
      "Card grading preparation done responsibly — evaluation, gentle cleaning where appropriate, and preservation-minded handling before you submit to PSA, CGC, BGS or SGC. No grade guaranteed.",
    serviceType: "Card grading preparation",
    eyebrow: "Card Services · Grading Prep",
    tone: "violet",
    h1: "CARD GRADING PREP.\nSHOW UP READY.",
    sub: "Preparation is not a grade. It's making sure your card presents at its best before an independent grader evaluates it under their own standards.",
    intro: [
      "Grading preparation is often misunderstood as a way to 'buy' a grade. It isn't. Preparation is the disciplined step of ensuring a card is clean of removable surface issues, handled without new damage, and presented honestly.",
      "The grade belongs to the grading company. Our job ends where theirs begins — at the point your card shows up ready.",
    ],
    impact: "THE GRADER MAKES THE CALL. WE MAKE SURE YOUR CARD SHOWS UP READY.",
    sections: [
      {
        heading: "WHAT PREP ACTUALLY INVOLVES",
        paragraphs: [
          "Individual evaluation of surface, edges, corners and centering presentation. Responsible removal of surface contaminants where possible. Preservation-minded handling and protective packaging for transit.",
          "What it never involves: altering the card, concealing damage, or promising an outcome.",
        ],
      },
    ],
    doList: [
      "Honest, individual evaluation of gradeability",
      "Responsible surface preparation where appropriate",
      "Preservation-minded handling and packaging",
      "Guidance on whether a card is worth submitting at all",
    ],
    dontList: [
      "We do not determine or guarantee grades.",
      "We do not trim, recolor or restore.",
      "We do not conceal defects to influence a grade.",
      "We do not promise acceptance by any grading company.",
    ],
    faqs: [
      {
        q: "What is card grading preparation?",
        a: "It's the step of evaluating a card, responsibly addressing removable surface issues, and handling it carefully so it presents at its best before an independent grading company evaluates it. It is not a grade and does not guarantee one.",
      },
      {
        q: "Does grading prep guarantee a higher grade?",
        a: "No. Independent grading companies determine all grades under their own standards. Preparation only ensures the card is presented honestly and free of removable surface issues.",
      },
      {
        q: "Which grading companies do you prep for?",
        a: "Our preparation is grader-agnostic — customers submit to PSA, CGC, BGS/Beckett and SGC. We are not affiliated with any grading company.",
      },
    ],
    related: [
      { label: "How It Works", to: "/how-it-works" },
      { label: "Our Process", to: "/our-process" },
      { label: "Results", to: "/results" },
    ],
  },

  "comic-cleaning": {
    path: "/comic-cleaning",
    seoTitle: "Comic Book Cleaning & Pressing Prep | CardScience HQ",
    metaDescription:
      "Professional comic book cleaning and preparation — surface care and preservation-minded handling for Bronze, Silver and modern books before grading. Individually evaluated. No grade guaranteed.",
    serviceType: "Comic book cleaning",
    eyebrow: "Comic Services · Cleaning",
    tone: "blue",
    h1: "COMIC CLEANING.\nHANDLED LIKE PAPER SHOULD BE.",
    sub: "Comics are paper, and paper is unforgiving. Every book is evaluated individually with preservation as the first principle.",
    intro: [
      "A comic book is a fundamentally different object than a trading card. Paper responds to moisture, pressure and handling in ways that are frequently irreversible — which is why comics demand their own dedicated process.",
      "CardScience HQ evaluates each book for surface issues that can be responsibly addressed while respecting the fragility of aged newsprint and modern glossy stock alike.",
    ],
    impact: "PAPER REMEMBERS EVERYTHING.",
    sections: [
      {
        heading: "BRONZE, SILVER AND MODERN BOOKS",
        paragraphs: [
          "Aged newsprint, spine stress, cover gloss and interior pages each require a distinct, careful approach. We never treat a Silver Age book the way we would a modern glossy cover.",
          "When a book is too fragile to work on responsibly, preservation is the answer — not processing.",
        ],
      },
    ],
    doList: [
      "Individual evaluation of every book",
      "Gentle, paper-appropriate surface care",
      "Preservation-minded handling and storage",
      "Documented handling throughout",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not use processes that stress fragile paper.",
      "We do not conceal spine or cover damage.",
      "We do not promise grading-company acceptance.",
    ],
    faqs: [
      {
        q: "Can comic books be cleaned before grading?",
        a: "Some surface issues can be responsibly addressed, but comics are paper and much of their condition is permanent. We evaluate every book individually and prioritize preservation.",
      },
      {
        q: "Do you handle vintage Silver and Bronze Age comics?",
        a: "Yes, with era-appropriate care. Aged newsprint is fragile and unforgiving, so vintage books receive the most conservative handling.",
      },
      {
        q: "Will cleaning improve my comic's grade?",
        a: "We never promise that. Grading companies determine grades under their own standards. We prepare the book responsibly; the grade is theirs.",
      },
    ],
    related: [
      { label: "Comic Preservation", to: "/comic-preservation" },
      { label: "Comic Services", to: "/comic-services" },
      { label: "How It Works", to: "/how-it-works" },
    ],
  },

  "comic-preservation": {
    path: "/comic-preservation",
    seoTitle: "Comic Book Preservation Service | CardScience HQ",
    metaDescription:
      "Comic book preservation — protective handling, storage guidance and preservation-minded preparation for your collection. Individually evaluated, confidential, mail-in nationwide.",
    serviceType: "Comic book preservation",
    eyebrow: "Comic Services · Preservation",
    tone: "cyan",
    h1: "COMIC PRESERVATION.\nPROTECT WHAT MATTERS.",
    sub: "Preservation isn't about changing a book — it's about keeping it from changing. For many comics, the most responsible service is protection, not processing.",
    intro: [
      "Not every book should be cleaned, and not every book should be graded. Sometimes the right service is simply protecting a comic from the forces that degrade paper over time.",
      "Our preservation-minded handling focuses on stabilizing condition and preventing avoidable damage — the childhood book, the first issue, the key that will never be sold.",
    ],
    impact: "THE BOOK MATTERS BECAUSE IT MATTERS TO YOU.",
    sections: [
      {
        heading: "PRESERVATION-FIRST HANDLING",
        paragraphs: [
          "We evaluate a book's current condition, handle it to avoid new stress, and provide guidance on protective storage that slows the natural aging of paper.",
          "Preservation is honest work: we protect what's there rather than promise what isn't.",
        ],
      },
    ],
    doList: [
      "Individual condition evaluation",
      "Preservation-minded handling and packaging",
      "Guidance on protective storage",
      "Confidential service, documented throughout",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not restore or rebuild a book.",
      "We do not conceal existing damage.",
      "We do not promise increased monetary value.",
    ],
    faqs: [
      {
        q: "What does comic preservation include?",
        a: "Individual condition evaluation, preservation-minded handling to avoid new damage, and guidance on protective storage that slows the natural aging of paper. It does not alter or restore the book.",
      },
      {
        q: "Should every comic be graded?",
        a: "No. Many books are best preserved rather than graded. We'll give you an honest read on what's appropriate for your book.",
      },
      {
        q: "Is my collection kept confidential?",
        a: "Yes. Client confidentiality is a core principle — see our Confidentiality policy for details.",
      },
    ],
    related: [
      { label: "Comic Cleaning", to: "/comic-cleaning" },
      { label: "Confidentiality", to: "/confidentiality" },
      { label: "Comic Services", to: "/comic-services" },
    ],
  },

  "mail-in-card-cleaning": {
    path: "/mail-in-card-cleaning",
    seoTitle: "Mail-In Card Cleaning Service (Nationwide) | CardScience HQ",
    metaDescription:
      "Mail-in card cleaning and grading prep, nationwide. Simple submission, documented chain of custody, insured return shipping guidance and confidential service. Evaluate before you send.",
    serviceType: "Mail-in card cleaning",
    eyebrow: "Card Services · Mail-In",
    tone: "violet",
    h1: "MAIL-IN CARD CLEANING.\nFROM ANYWHERE IN THE COUNTRY.",
    sub: "You don't need to be local. Our mail-in process is built around documentation, protection and confidentiality from the moment you submit to the moment your card returns.",
    intro: [
      "Most collectors aren't near a lab — so the lab comes to them. Our mail-in service is designed to make sending a collectible feel as safe as handing it over in person.",
      "Every submission is evaluated individually. Nothing is shipped before your submission is reviewed, and shipping and insurance guidance is provided based on declared value.",
    ],
    impact: "DOCUMENTED FROM INTAKE TO RETURN.",
    sections: [
      {
        heading: "HOW MAIL-IN WORKS",
        paragraphs: [
          "Submit your collectible details online. We review and respond with next steps — do not ship before your submission is approved. Once received, your item is logged, evaluated, handled and documented, then returned with appropriate protection.",
          "Declared values of $50+ receive documentation guidance; $500+ receive insured-shipping guidance.",
        ],
      },
    ],
    doList: [
      "Nationwide mail-in submission",
      "Documented chain of custody, intake to return",
      "Shipping and insurance guidance by declared value",
      "Confidential handling throughout",
    ],
    dontList: [
      "Do not ship before your submission is approved.",
      "We do not promise any specific grade.",
      "We do not guarantee grading-company acceptance.",
      "We do not share your collection publicly.",
    ],
    faqs: [
      {
        q: "How does mail-in card cleaning work?",
        a: "Submit your collectible details online first. We review and send next steps — never ship before approval. Once received, your item is logged, evaluated, handled and documented, then returned with appropriate protection.",
      },
      {
        q: "Is it safe to mail high-value cards?",
        a: "We provide documentation guidance for declared values of $50+ and insured-shipping guidance for $500+. Everything is documented and handled confidentially. Insurance decisions ultimately rest with you and your carrier.",
      },
      {
        q: "Do you serve the whole country?",
        a: "Yes. Mail-in service is available nationwide — you do not need to be local.",
      },
    ],
    related: [
      { label: "Shipping & Insurance", to: "/shipping-insurance" },
      { label: "How It Works", to: "/how-it-works" },
      { label: "Submit A Collectible", to: "/submit" },
    ],
  },

  "high-value-card-care": {
    path: "/high-value-card-care",
    seoTitle: "High-Value Card Care & Concierge Prep | CardScience HQ",
    metaDescription:
      "Concierge-level care for high-value cards and collectibles — individual evaluation, documented handling, confidential service and insured-shipping guidance. No grade guaranteed.",
    serviceType: "High-value card care",
    eyebrow: "Card Services · High-Value",
    tone: "violet",
    h1: "HIGH-VALUE CARD CARE.\nHANDLED ACCORDINGLY.",
    sub: "When the stakes are high, the process matters more, not less. Documented handling, confidentiality and disciplined evaluation for your most important collectibles.",
    intro: [
      "A five-figure card and a childhood common deserve the same discipline — but a high-value collectible carries additional expectations around documentation, security and communication.",
      "For high-value submissions we apply the same gentle-by-design principles with heightened documentation and insured-shipping guidance, so nothing about the process is left to chance.",
    ],
    impact: "THE HIGHER THE STAKES, THE MORE THE PROCESS MATTERS.",
    sections: [
      {
        heading: "CONCIERGE-LEVEL DISCIPLINE",
        paragraphs: [
          "Individual evaluation, documented chain of custody, confidential handling and insured-shipping guidance for declared values of $500+. Clear communication at each step.",
          "We would rather turn away work than take a risk we can't justify with a high-value collectible.",
        ],
      },
    ],
    doList: [
      "Heightened documentation and chain of custody",
      "Confidential, individual evaluation",
      "Insured-shipping guidance for $500+ declared value",
      "Honest guidance on whether to proceed at all",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not take risks we can't justify.",
      "We do not share client holdings publicly.",
      "We do not guarantee monetary outcomes.",
    ],
    faqs: [
      {
        q: "How do you handle high-value collectibles?",
        a: "With heightened documentation, confidential handling, individual evaluation and insured-shipping guidance for declared values of $500+. If we can't proceed responsibly, we'll say so.",
      },
      {
        q: "Is my high-value collection kept private?",
        a: "Yes. Client confidentiality is a core principle. We do not publicly display or discuss client holdings.",
      },
      {
        q: "Do you guarantee the card's value will increase?",
        a: "No. We never promise increased monetary value or any specific grade. We provide disciplined, documented care.",
      },
    ],
    related: [
      { label: "Confidentiality", to: "/confidentiality" },
      { label: "Shipping & Insurance", to: "/shipping-insurance" },
      { label: "Trust Center", to: "/trust-center" },
    ],
  },

  "dallas-card-cleaning": {
    path: "/dallas-card-cleaning",
    seoTitle: "Dallas Card Cleaning & Grading Prep | CardScience HQ",
    metaDescription:
      "Card cleaning and grading preparation for Dallas–Fort Worth collectors. Individually evaluated, gentle-by-design care with convenient mail-in service across DFW and nationwide. No grade guaranteed.",
    serviceType: "Card cleaning in Dallas",
    eyebrow: "Card Services · Dallas–Fort Worth",
    tone: "blue",
    h1: "CARD CLEANING FOR DALLAS.\nBUILT FOR DFW COLLECTORS.",
    sub: "Dallas–Fort Worth has one of the most active collecting communities in the country. Our lab-grade process is available to DFW collectors with simple, documented mail-in service.",
    intro: [
      "From Deep Ellum card shows to the DFW show circuit, Dallas collectors take the hobby seriously — and so do we. Our process brings disciplined, gentle-by-design care to the Metroplex.",
      "Whether you're in Dallas, Fort Worth, Plano, Arlington or Frisco, submission is simple: every collectible is evaluated individually and handled with a documented chain of custody.",
    ],
    impact: "LAB-GRADE CARE. LOCAL TO THE METROPLEX.",
    sections: [
      {
        heading: "SERVING THE DFW COLLECTING COMMUNITY",
        paragraphs: [
          "Dallas, Fort Worth, Plano, Frisco, Arlington, Irving and the wider Metroplex. Convenient mail-in submission with documentation and insured-shipping guidance based on declared value.",
          "The same standards we apply to every card apply here: honest evaluation, responsible cleaning where appropriate, and no promises we can't keep.",
        ],
      },
    ],
    doList: [
      "Convenient mail-in service across DFW",
      "Individual evaluation of every collectible",
      "Documented chain of custody",
      "Shipping and insurance guidance by declared value",
    ],
    dontList: [
      "We do not promise any specific grade.",
      "We do not use harsh chemicals or direct heat.",
      "We do not trim, recolor or restore.",
      "We do not guarantee grading-company acceptance.",
    ],
    faqs: [
      {
        q: "Do you offer card cleaning in Dallas?",
        a: "Yes. We serve Dallas–Fort Worth collectors with convenient, documented mail-in service across the Metroplex — Dallas, Fort Worth, Plano, Frisco, Arlington and beyond.",
      },
      {
        q: "Do I have to drop cards off in person?",
        a: "No. Our process is mail-in and built around documentation, protection and confidentiality, so you can submit from anywhere in DFW or nationwide.",
      },
      {
        q: "Will this guarantee a better grade for my card?",
        a: "No. Grades are determined independently by grading companies. We prepare the card responsibly; the grade is theirs alone.",
      },
    ],
    related: [
      { label: "Mail-In Card Cleaning", to: "/mail-in-card-cleaning" },
      { label: "Card Grading Prep", to: "/card-grading-prep" },
      { label: "How It Works", to: "/how-it-works" },
    ],
  },
};

export const SEO_PAGE_SLUGS = Object.keys(SEO_PAGES);
